import React from "react";
import { Button, dialog, InputString } from "../../nq-component";
import {
  addSchemaUseCase,
  updateSchemaUseCase,
  deleteSchemaUseCase,
} from "../../usecases/schema/usecases";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../usecases/object";
import { exportCSVUseCase } from "../../usecases/csv/usecases";
import { Progress, InfiniteScroll } from "../../nq-component";
import withRouter from "../../withRouter";

import NavBar from "../../components/navbar";

import DialogCreate from "./components/DialogCreate";
import DialogDelete from "./components/DialogDelete";

import BaseListPage from "../../base/BaseListPage";

import Calendar from "../../components/CoordinatorCalendar";
import DialogSuccess from "./components/DialogSuccess";

import MyCalendarHrPagePresenter from "./MyCalendarHrPagePresenter";

class MyCalendarHrPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new MyCalendarHrPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      exportCSVUseCase(),
      addSchemaUseCase(),
      updateSchemaUseCase(),
      deleteSchemaUseCase()
    );
    this.state = {
      objects: [],
      selected: [],
      loading: false,
      total: 0,
      count: 0,
      showDetails: false,
      eventDetails: {},
      events: {}, // Store calendar events here
      selectedDate: null,
      selectedEvent: null,
      calendarEvents: {},
    };
  }

  getCollectionName() {
    return "hrCalendar";
  }

  componentDidMount() {
    this.fetchEvents();
  }

  async fetchEvents() {
    try {
      const events = await this.presenter.getAllEvents();
      console.log("Fetched events from database:", events);

      // Format the events properly for the Calendar component
      const formattedEvents = {};

      Object.keys(events).forEach((dateKey) => {
        // The Calendar component expects keys in the format "YYYY-M-D"
        const eventList = events[dateKey];
        if (Array.isArray(eventList) && eventList.length > 0) {
          eventList.forEach((event) => {
            if (event.startDate) {
              // Parse the date from the event's startDate
              const date = new Date(event.startDate);
              // Format the date key as expected by the Calendar component
              const formattedDateKey = `${date.getFullYear()}-${
                date.getMonth() + 1
              }-${date.getDate()}`;

              if (!formattedEvents[formattedDateKey]) {
                formattedEvents[formattedDateKey] = [];
              }

              // Add properly formatted event with all required fields
              formattedEvents[formattedDateKey].push({
                id: event.id,
                description: event.description,
                startDate: event.startDate,
                endDate: event.endDate || event.startDate, // Default to startDate if endDate is missing
                startTime: event.startTime || "00:00",
                endTime: event.endTime || "23:59",
              });
            }
          });
        }
      });

      this.setState({ calendarEvents: formattedEvents });
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }

  onChangeObject(value, field) {
    this.setState((prevState) => ({
      eventName: {
        ...prevState.eventName,
        [field]: value,
      },
    }));
  }

  // Example method to handle date selections
  handleDateSelect = (date) => {
    console.log("Selected date:", date);
    this.setState({ selectedDate: date });
  };

  handleEventAdd = async (date, eventData) => {
    const currentUser = this.getCurrentUser();

    const newEvent = {
      ...eventData,
      id: eventData.id || new Date().getTime(),
      hr: {
        id: currentUser.id,
      },
    };

    try {
      await this.presenter.handleSubmit(newEvent);

      this.setState((prevState) => {
        const dateKey = eventData.startDate;
        const updatedEvents = { ...prevState.calendarEvents };

        if (!updatedEvents[dateKey]) {
          updatedEvents[dateKey] = [];
        }

        updatedEvents[dateKey] = [...updatedEvents[dateKey], newEvent];

        return { calendarEvents: updatedEvents };
      });

      setTimeout(async () => {
        const updatedEvents = await this.presenter.getAllEvents();
        this.setState({ calendarEvents: updatedEvents });
      }, 500);
    } catch (error) {
      console.error("Failed to add event:", error);
    }
  };

  handleEventDelete = async (eventId) => {
    try {
      // First update the UI immediately
      this.setState((prevState) => {
        const updatedEvents = { ...prevState.calendarEvents };

        // Remove the event from all date keys
        Object.keys(updatedEvents).forEach((dateKey) => {
          updatedEvents[dateKey] = updatedEvents[dateKey].filter(
            (event) => event.id !== eventId
          );

          // Remove empty date arrays
          if (updatedEvents[dateKey].length === 0) {
            delete updatedEvents[dateKey];
          }
        });

        return { calendarEvents: updatedEvents };
      });

      // Then perform the database operation
      await this.presenter.deleteEvent(eventId);

      // Finally refresh from database
      const updatedEvents = await this.presenter.getAllEvents();
      this.setState({ calendarEvents: updatedEvents });

      console.log("Event deleted successfully:", eventId);
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  clickData(object, index) {
    this.setState({ showDetails: true, eventDetails: object });
  }

  hideFilter() {
    this.setState({ showDetails: false });
  }

  onClickView() {
    const { eventDetails } = this.state;
    this.presenter.onClickView(eventDetails);
  }

  handleDelete() {
    const { eventDetails } = this.state;
    this.presenter.onClickDelete(eventDetails);
  }

  onChangeReference(value, field) {
    const schema = this.getSchema(this.getCollectionName());
    this.presenter.onChangeReference(value, schema);
  }

  successModal(message) {
    dialog.fire({
      html: <DialogSuccess message={message} />,
      footer: false,
    });
  }

  onClickAdd(object) {
    const schema = this.getSchemas();

    dialog.fire({
      html: (
        <DialogCreate
          schema={schema}
          objects={object}
          onChangeData={this.onChangeObject.bind(this)}
          handleSubmit={this.handleSubmit.bind(this)}
        />
      ),
      footer: false,
    });
  }

  onClickDelete() {
    const { eventDetails } = this.state;
    dialog.fire({
      html: (
        <>
          <DialogDelete
            eventDetails={eventDetails}
            clickDelete={this.handleDelete.bind(this)}
            clickClose={() => dialog.close()}
          />
        </>
      ),
      footer: false,
    });
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());
    const { objects, selected, count, progress, eventDetails, formData } =
      this.state;

    if (!schema) return <Progress />;
    const user = this.getCurrentUser();

    return (
      <>
        <NavBar user={user} />
        <div className="overflow-auto">
          <InfiniteScroll
            className="h-100"
            loadMore={this.loadMore.bind(this)}
            hasMore={!progress && count > objects.length}
          >
            <div className="p-4">
              <div className="justify-content-between align-items-center">
                <h3 className="fw-bold text-capitalize">My Calendar</h3>
                <Calendar
                  events={this.state.calendarEvents}
                  initialDate={this.state.selectedDate || new Date()}
                  onEventsChange={this.handleEventsChange}
                  onDateSelect={this.handleDateSelect.bind(this)}
                  onEventAdd={this.handleEventAdd}
                  onEventDelete={this.handleEventDelete}
                />
              </div>
            </div>
          </InfiniteScroll>
        </div>
      </>
    );
  }
}

export default withRouter(MyCalendarHrPage);
